import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { Bets } from '../../../config/bets';

import { BetInputData, BetStepperType, QuickBetClick } from './types';

const initialState: BetStepperType = {
  betInputValues: {
    [Bets.ONE]: '0.00',
    [Bets.TWO]: '0.00',
  },
  lastBetInputValues: {
    [Bets.ONE]: '0.00',
    [Bets.TWO]: '0.00',
  },
  isBetFinalized: {
    [Bets.ONE]: true,
    [Bets.TWO]: true,
  },
  quickBetClickedOn: {
    [Bets.ONE]: { click: false, count: 0 },
    [Bets.TWO]: { click: false, count: 0 },
  },
  betInputStack: {
    [Bets.ONE]: {
      stack: [],
      isReset: false,
    },
    [Bets.TWO]: {
      stack: [],
      isReset: false,
    },
  },
};

const isSameAsLastValue = (lastValue: string, presentValue: string): boolean => {
  return lastValue === presentValue;
};

export const betStepperSlice = createSlice({
  name: 'betStepper',
  initialState: initialState,
  reducers: {
    initBetInputValues: (state, action: PayloadAction<Record<string, string>>) => {
      state.betInputValues = action.payload;
      state.lastBetInputValues = action.payload;
      state.isBetFinalized[Bets.ONE] = true;
      state.isBetFinalized[Bets.TWO] = true;
      state.betInputStack[Bets.ONE].stack.push(action.payload[Bets.ONE]);
      state.betInputStack[Bets.TWO].stack.push(action.payload[Bets.TWO]);
    },
    setBetInputValue: (state, action: PayloadAction<BetInputData>) => {
      state.betInputValues = {
        ...state.betInputValues,
        [action.payload.betId]: action.payload.value,
      };
      if (action.payload.isBetFinalized) {
        state.isBetFinalized[action.payload.betId] = true;
        state.lastBetInputValues[action.payload.betId] = action.payload.value;

        const lastValue = state.betInputStack[action.payload.betId].stack[state.betInputStack[action.payload.betId].stack.length-1] ?? '';
        if (!isSameAsLastValue(lastValue, action.payload.value)) {
          state.betInputStack[action.payload.betId].stack.push(action.payload.value);
          state.betInputStack[action.payload.betId].isReset = false;
        }
      } else {
        state.isBetFinalized[action.payload.betId] = false;
      }
    },
    resetBetInputValue: (state, action: PayloadAction<number>) => {
      state.betInputValues = {
        ...state.betInputValues,
        [action.payload]: '0.00',
      };
    },
    setQuickBetClick: (state, action: PayloadAction<QuickBetClick>) => {
      state.quickBetClickedOn = {
        [Bets.ONE]: {
          click: false,
          count: 0,
        },
        [Bets.TWO]: {
          click: false,
          count: 0,
        },
        [action.payload.betId]: action.payload.value,
      };
    },
    undoBetInput: (state, action: PayloadAction<Bets>) => {
      const betId = action.payload;
      const betInputStack = [...state.betInputStack[betId].stack];
      if (betInputStack.length <= 0) return;

      state.betInputStack[betId].stack.pop();
      const previousValue = state.betInputStack[betId].stack.at(-1);
      state.betInputValues = {
        ...state.betInputValues,
        [betId]: previousValue,
      };
      state.isBetFinalized = {
        ...state.isBetFinalized,
        [betId]: true,
      };
    },
    resetBetStack: (state, action: PayloadAction<Bets>) => {
      const betId = action.payload;
      const isReset = state.betInputStack[betId].isReset;
      if (!isReset) {
        state.betInputStack[betId].stack = state.betInputStack[betId].stack.slice(-1);
        state.betInputStack[betId].isReset = true;
      }
    },
  },
});

export const betStepperActions = betStepperSlice.actions;
